import { useEffect } from 'react'
import { Button } from '../../catalyst/button'
import { Description, Field as FormField, FieldGroup, Label } from '../../catalyst/fieldset'
import { Input } from '../../catalyst/input'
import { Text } from '../../catalyst/text'
import { api, dropNulls } from '../../lib/api'
import { areaInputValue, areaUnitLabel } from '../../lib/area'
import { usePesticideDetail } from '../../lib/pesticides'
import { amountFromRate } from '../../lib/rates'
import type { SprayLog } from '../../lib/types'
import { answerLabel, ChoiceButtons, SameAsLast, YesNo } from '../chips'
import { ProductPicker } from '../../lib/product-picker'
import { blueberryUse, canSuggest, LabelFacts } from '../label-facts'
import { amountProblem, optNum, optText, seasonYearOf, str, type FlowDef, type StepCtx } from '../flow'
import { AmountInput } from './shared'
import { rememberWho, WhoField } from '../who'

const METHODS = ['ground', 'air', 'foliar', 'furrow', 'seed', 'other'] as const
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

type Product = {
  productName: string
  /** Set when the grower picked a registered product (the pesticide registry's id and PCP number). */
  productId: string
  pcpNumber: string
  activeIngredients: string
  quantityUsed: string
  quantityUnit: string
  rateApplied: string
  rateUnit: string
  phiDays: string
  /** H1: the product was applied following its label's directions. Unanswered until the grower says. */
  labelFollowed?: boolean
  /** The grower typed the amount, so it no longer follows rate × area. */
  quantityEdited?: boolean
}
const emptyProduct = (): Product => ({ productName: '', productId: '', pcpNumber: '', activeIngredients: '', quantityUsed: '', quantityUnit: 'kg', rateApplied: '', rateUnit: 'kg/ha', phiDays: '' })

const productsOf = (v: StepCtx['values']): Product[] => (Array.isArray(v.products) ? (v.products as Product[]) : [emptyProduct()])

async function fetchLastSpray(orgId: string, farmId: string, fieldId: string | undefined): Promise<SprayLog | null> {
  if (!fieldId) return null
  const url = `/orgs/${orgId}/farms/${farmId}/spray-logs`
  const year = new Date().getFullYear()
  for (const seasonYear of [year, year - 1]) {
    const { data } = await api.getPage<SprayLog>(`${url}?fieldId=${fieldId}&seasonYear=${seasonYear}&limit=1`)
    if (data[0]) return api.get<SprayLog>(`${url}/${data[0].id}`)
  }
  return null
}

/** The area being treated, as a number in the farm's unit, or undefined while blank. */
const areaOf = (v: StepCtx['values']) => optNum(v.areaTreated)

/** The amount a product's rate comes to over the area, unless the grower typed their own amount. */
function autoAmount(p: Product, area: number | undefined, areaUnit: StepCtx['areaUnit']) {
  const rate = optNum(p.rateApplied)
  if (rate == null || area == null) return null
  return amountFromRate(rate, p.rateUnit, area, areaUnit)
}

export function SuggestButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button type="button" className="min-h-9 rounded-md px-2 text-left text-sm/6 font-medium text-emerald-700 ring-1 ring-emerald-600/20 hover:bg-emerald-50 dark:text-emerald-400 dark:ring-emerald-500/30 dark:hover:bg-emerald-900/20" onClick={onClick}>
      {children}
    </button>
  )
}

/** One product in the mix: what it is, what its label says, and how much went on. */
function ProductFields({ p, i, products, ctx }: { p: Product; i: number; products: Product[]; ctx: StepCtx }) {
  const { values, patch, areaUnit } = ctx
  const detail = usePesticideDetail(p.productId || undefined)
  const label = detail.pesticide?.label.status === 'ready' ? detail.pesticide.label : null
  const use = label ? blueberryUse(label) : null
  const suggest = !!label && canSuggest(label) && !!use
  const update = (partial: Partial<Product>) => patch({ products: products.map((x, idx) => (idx === i ? { ...x, ...partial } : x)) })
  const area = areaOf(values)
  const computed = autoAmount(p, area, areaUnit)

  // A checked label that names one application method fills it in, until the grower picks one.
  const labelMethod = suggest ? use.method : null
  useEffect(() => {
    if (labelMethod && values.method === undefined) patch({ method: labelMethod, methodFromLabel: true })
  }, [labelMethod, values.method, patch])

  return (
    <fieldset className="space-y-4 rounded-lg p-4 ring-1 ring-zinc-950/10 dark:ring-white/10">
      <FormField>
        <Label>Product</Label>
        <ProductPicker
          name={p.productName}
          picked={p.productId ? p : null}
          // Typing again unlinks a picked product: the name no longer matches the registration.
          onName={(name) => update({ productName: name, productId: '', pcpNumber: '', activeIngredients: '' })}
          onPick={(picked) => update(picked)}
        />
      </FormField>
      {p.productId && <LabelFacts detail={detail} />}
      <div className="flex gap-3">
        <FormField className="flex-1">
          <Label>Rate</Label>
          <AmountInput value={p.rateApplied} onChange={(r) => update({ rateApplied: r })} />
        </FormField>
        <FormField className="w-28">
          <Label>Per</Label>
          <Input value={p.rateUnit} onChange={(e) => update({ rateUnit: e.target.value })} />
        </FormField>
      </div>
      {suggest && use.rates.length > 0 && (
        <div className="-mt-2 flex flex-wrap gap-2">
          {use.rates.slice(0, 4).map((r) => (
            <SuggestButton key={`${r.value} ${r.unit}`} onClick={() => update({ rateApplied: String(r.value), rateUnit: r.unit })}>
              Use {r.value} {r.unit} (label)
            </SuggestButton>
          ))}
        </div>
      )}
      <div className="flex gap-3">
        <FormField className="flex-1">
          <Label>Amount used</Label>
          <AmountInput value={p.quantityUsed} onChange={(q) => update({ quantityUsed: q, quantityEdited: true })} />
        </FormField>
        <FormField className="w-28">
          <Label>Unit</Label>
          <Input value={p.quantityUnit} onChange={(e) => update({ quantityUnit: e.target.value, quantityEdited: true })} />
        </FormField>
      </div>
      {!p.quantityEdited && computed ? (
        <Text className="-mt-2">
          = {p.rateApplied} {p.rateUnit} × {str(values.areaTreated)} {areaUnitLabel(areaUnit)}. Change it if you used a different amount.
        </Text>
      ) : p.quantityEdited && autoAmount({ ...p, quantityEdited: false }, area, areaUnit) ? (
        <div className="-mt-2">
          <SuggestButton onClick={() => update({ quantityEdited: false })}>Work it out from the rate</SuggestButton>
        </div>
      ) : null}
      <FormField>
        <Label>Pre-harvest interval (PHI), days</Label>
        <Description>From the label. Sets the earliest date you can pick this field.</Description>
        <Input type="number" inputMode="numeric" min="0" step="1" value={p.phiDays} onChange={(e) => update({ phiDays: e.target.value })} />
      </FormField>
      {suggest && use.phi && String(use.phi.max) !== p.phiDays && (
        <div className="-mt-2">
          {/* Labels can give different PHIs by target; the longest is the one that is safe for all of them. */}
          <SuggestButton onClick={() => update({ phiDays: String(use.phi!.max) })}>
            Use {use.phi.max} day{use.phi.max === 1 ? '' : 's'} ({use.phi.min === use.phi.max ? 'label' : `longest on the label, ${use.phi.min}–${use.phi.max} by target`})
          </SuggestButton>
        </div>
      )}
      <YesNo label="Did you follow the label instructions?" value={p.labelFollowed} onChange={(a) => update({ labelFollowed: a })} />
      {products.length > 1 && (
        <Button plain onClick={() => patch({ products: products.filter((_, idx) => idx !== i) })}>
          Remove this product
        </Button>
      )}
    </fieldset>
  )
}

function ApplyStep(ctx: StepCtx) {
  const { values, patch, last, field, areaUnit } = ctx
  const products = productsOf(values)
  const lastSpray = last as SprayLog | null
  const lastNames = lastSpray?.products?.map((p) => p.productName).join(', ')
  const area = areaOf(values)

  // Default the area treated to the whole field, once.
  useEffect(() => {
    if (values.areaTreated === undefined && field) patch({ areaTreated: areaInputValue(field.areaM2, areaUnit) })
  }, [values.areaTreated, field, areaUnit, patch])

  // Keep each amount equal to rate × area until the grower types their own. One effect for all
  // products, so two products updating at once can't overwrite each other.
  useEffect(() => {
    const next = products.map((p) => {
      const c = p.quantityEdited ? null : autoAmount(p, area, areaUnit)
      if (!c || (String(c.quantity) === p.quantityUsed && c.unit === p.quantityUnit)) return p
      return { ...p, quantityUsed: String(c.quantity), quantityUnit: c.unit }
    })
    if (next.some((p, i) => p !== products[i])) patch({ products: next })
  }, [products, area, areaUnit, patch])

  return (
    <FieldGroup>
      {lastSpray?.products?.length ? (
        <SameAsLast
          label={lastNames ?? ''}
          onClick={() =>
            patch({
              products: lastSpray.products!.map((p) => ({
                productName: p.productName,
                productId: p.productId ?? '',
                pcpNumber: p.pcpNumber ?? '',
                activeIngredients: '',
                quantityUsed: String(p.quantityUsed),
                quantityUnit: p.quantityUnit,
                rateApplied: p.rateApplied == null ? '' : String(p.rateApplied),
                rateUnit: p.rateUnit ?? 'kg/ha',
                phiDays: p.phiDays == null ? '' : String(p.phiDays),
                labelFollowed: p.labelFollowed ?? undefined,
                // With a rate, the amount is worked out again for today's area; without one, keep last time's.
                quantityEdited: p.rateApplied == null,
              })),
            })
          }
        />
      ) : null}
      <FormField>
        <Label>Area treated ({areaUnitLabel(areaUnit)})</Label>
        <Input type="number" inputMode="decimal" min="0" step="any" value={str(values.areaTreated)} onChange={(e) => patch({ areaTreated: e.target.value })} />
      </FormField>
      {products.map((p, i) => (
        <ProductFields key={i} p={p} i={i} products={products} ctx={ctx} />
      ))}
      <Button outline className="w-full" onClick={() => patch({ products: [...products, emptyProduct()] })}>
        + Add another product
      </Button>
    </FieldGroup>
  )
}

function HowStep(ctx: StepCtx) {
  const { values, patch, last } = ctx
  const lastSpray = last as SprayLog | null
  // No method from the label: start from ground, the most common.
  useEffect(() => {
    if (values.method === undefined) patch({ method: 'ground' })
  }, [values.method, patch])

  return (
    <FieldGroup>
      {lastSpray?.applicatorName || lastSpray?.applicationMethod ? (
        <SameAsLast
          label={[cap(lastSpray.applicationMethod), lastSpray.applicatorName].filter(Boolean).join(', ')}
          onClick={() => patch({ method: lastSpray.applicationMethod, methodFromLabel: false, applicator: lastSpray.applicatorName ?? '' })}
        />
      ) : null}
      <div>
        <ChoiceButtons
          label="How was it applied?"
          value={str(values.method) as (typeof METHODS)[number] | ''}
          options={METHODS.map((m) => ({ value: m, label: cap(m) }))}
          onChange={(m) => patch({ method: m, methodFromLabel: false })}
        />
        {values.methodFromLabel === true && <Text className="mt-2">From the label. Change it if you applied it another way.</Text>}
      </div>
      <FormField>
        <Label>Weather</Label>
        <Input value={str(values.weather)} onChange={(e) => patch({ weather: e.target.value })} placeholder="Clear, 15°C, light wind" />
      </FormField>
      <WhoField ctx={ctx} />
    </FieldGroup>
  )
}

const validProducts = (v: StepCtx['values']) =>
  productsOf(v).filter((p) => p.productName.trim() && Number(p.quantityUsed) > 0 && p.quantityUnit.trim())

export const sprayFlow: FlowDef = {
  kind: 'spray',
  checkForm: 'h1',
  label: 'Sprayed something',
  needsField: true,
  // The method starts unset so a checked label can fill it in (ProductFields); HowStep falls back to ground.
  initial: ({ today, fieldId }) => ({ fieldId, date: today, products: [emptyProduct()] }),
  fetchLast: fetchLastSpray,
  steps: [
    {
      id: 'tank',
      title: 'What did you apply?',
      // A product with an amount or rate that can't be saved holds the step, rather than being dropped.
      valid: (v) => validProducts(v).length > 0 && productsOf(v).every((p) => !amountProblem(p.quantityUsed) && !amountProblem(p.rateApplied)),
      render: (ctx) => <ApplyStep {...ctx} />,
    },
    {
      id: 'how',
      title: 'How was it applied?',
      valid: (v) => str(v.method) !== '',
      render: (ctx) => <HowStep {...ctx} />,
    },
  ],
  review: ({ values, field, areaUnit }) => ({
    title: `Sprayed${field ? ` · ${field.name}` : ''}`,
    lines: [
      { label: 'Date', value: str(values.date) },
      ...validProducts(values).map((p) => ({
        label: p.productName,
        value: [`${p.quantityUsed} ${p.quantityUnit}`, p.rateApplied && `${p.rateApplied} ${p.rateUnit}`, p.phiDays && `PHI ${p.phiDays} day${p.phiDays === '1' ? '' : 's'}`, `label followed: ${answerLabel(p.labelFollowed).toLowerCase()}`].filter(Boolean).join(', '),
      })),
      { label: 'Method', value: cap(str(values.method)) },
      ...(optNum(values.areaTreated) != null ? [{ label: 'Area', value: `${str(values.areaTreated)} ${areaUnitLabel(areaUnit)}` }] : []),
      ...(optText(values.weather) ? [{ label: 'Weather', value: str(values.weather) }] : []),
      ...(optText(values.applicator) ? [{ label: 'Applied by', value: str(values.applicator) }] : []),
    ],
  }),
  entry: (ctx) => {
    const { values, areaUnit } = ctx
    const date = str(values.date)
    const area = optNum(values.areaTreated)
    return dropNulls({
      fieldId: str(values.fieldId),
      seasonYear: seasonYearOf(date),
      appliedOn: date,
      applicationMethod: str(values.method),
      areaTreated: area ?? null,
      areaUnit: area == null ? null : areaUnit,
      weatherConditions: optText(values.weather) ?? null,
      applicatorName: optText(values.applicator) ?? null,
      products: validProducts(values).map((p) => {
        const phi = optNum(p.phiDays)
        const rate = optNum(p.rateApplied)
        return {
          productName: p.productName.trim(),
          productId: p.productId || undefined,
          pcpNumber: p.pcpNumber || undefined,
          quantityUsed: Number(p.quantityUsed),
          quantityUnit: p.quantityUnit.trim(),
          rateApplied: rate,
          rateUnit: rate == null ? undefined : optText(p.rateUnit),
          // Only the PHI: the server derives the EAHD from it and the application date, so the two
          // can't disagree if the date is edited later.
          phiDays: phi,
          // Unanswered stays unset, so the record shows it as missing rather than as "No".
          labelFollowed: p.labelFollowed,
        }
      }),
    })
  },
  badge: { framework: 'CanadaGAP', formCode: 'H1', formName: 'Agronomic Inputs (Agricultural Chemicals)' },
  historyPath: (farmId, fieldId) => `/farms/${farmId}/fields/${fieldId}/spray-logs`,
  onRecorded: rememberWho(),
}
